/**
 * Trees — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_motifs_ii`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const treesPack: ToilePack = {
    slug: 'trees',
    category: 'motif',
    name: 'Trees',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'oak', label: 'Oak in leaf', subject: 'A single broad oak in full summer leaf standing alone on a plain ground, a wide trunk, spreading branches, a dense rounded crown, a small shadow.'},
        {key: 'bare', label: 'Bare winter tree', subject: 'A single bare tree with every branch and twig drawn, a few last leaves, a crow on a branch, on a plain pale ground.'},
        {key: 'seasons', label: 'Four seasons', subject: 'The same tree drawn four times in a row — blossoming, in full green leaf, in orange autumn colour, bare with snow — on a plain ground.'},
    ],
    styles: [
        {key: 'line', label: 'Line drawing', style: 'single-weight black line drawing, clean contour, white ground', palette: 'black ink only, no other colour', framing: 'centred on a plain white background with a wide even margin'},
        {key: 'flat', label: 'Flat', style: 'flat vector illustration, bold simplified shapes, limited palette, plain ground', palette: 'a limited palette of green and brown, flat and bright', framing: 'centred on a plain white background with generous padding around the shape'},
        {key: 'watercolour', label: 'Watercolour', style: 'soft watercolour illustration, gentle washes, white paper', palette: 'green, gold and grey washes across the four seasons', framing: 'set on a plain white paper background, the wash fading toward a soft margin'},
    ],
    directions: ['a family tree instead', 'in autumn colours only', 'for a nursery print'],
    directionHint: 'try: a family tree instead',
    seo: {
        title: 'Tree art: oak, bare winter, four seasons',
        metaDescription: 'Nine tree illustrations — an oak in leaf, a bare winter tree and the same tree through four seasons — in line, flat and watercolour styles.',
        h1: 'Tree drawings: oak, bare winter, four seasons in three styles',
        tagline: 'Nine renders, three tree readings, three looks each — line to watercolour.',
        intro: 'The single tree, held back from the forest scenes of the last motif wave: a broad oak in summer leaf, a bare winter tree with every twig drawn, and the same tree across four seasons in a row. Each renders as clean line, a flat vector, or soft watercolour washes. Ask for a family-tree version and the same three looks carry it.',
        useCases: [
            'a family-tree card or keepsake',
            'a nursery or living-room print',
            'a seasonal calendar or poster',
            'a social media nature post',
        ],
        faq: [
            {q: 'What size do these print at?', a: 'Each is composed in a tall portrait crop, suited to a poster, a print or a card.'},
            {q: 'Can I get a family-tree version for names?', a: 'Yes — the direction field takes any variation, and the same three styles render it.'},
            {q: "What's the difference between the three styles?", a: 'Line is a single black contour on white, flat holds a bright green-and-brown palette, and watercolour washes across the four seasons.'},
            {q: 'Is this a real photograph of a tree?', a: 'No — every image is an illustration, drawn in the chosen style, not a photo.'},
        ],
    },
}
